import React from "react";

import ActionBar from "./ActionBar";
import type { Action } from "./action";
import { cn } from "./cn";

type Props = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  /** Small counter chip shown next to the title (e.g. "248 items"). */
  count?: React.ReactNode;
  actions?: Action[];
  className?: string;
};

/**
 * Unified page header: brand-tinted icon square + title + subtitle, with an
 * optional action cluster on the right.
 */
const PageHeader: React.FC<Props> = ({
  title,
  subtitle,
  icon,
  count,
  actions,
  className,
}) => (
  <header
    className={cn(
      "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
      className,
    )}
  >
    <div className="flex min-w-0 items-start gap-3">
      {icon ? (
        <span
          aria-hidden
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand ring-1 ring-inset ring-brand/15"
        >
          {icon}
        </span>
      ) : null}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="truncate text-[20px] font-semibold tracking-tight text-gray-900 sm:text-[22px]">
            {title}
          </h1>
          {count !== undefined && count !== null ? (
            <span className="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-100 px-2 text-[10.5px] font-semibold tabular-nums text-gray-600">
              {count}
            </span>
          ) : null}
        </div>
        {subtitle ? (
          <p className="mt-1 truncate text-[12.5px] text-gray-500">
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>

    {actions && actions.length > 0 ? (
      <ActionBar actions={actions} />
    ) : null}
  </header>
);

export default PageHeader;
